// src/ClinicalDataCollector/components/output/LabsSummary.jsx
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Grid,
  Alert,
  Button,
  Collapse,
} from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import { useSelector } from 'react-redux';
import { AdvancedMdContext } from '../../../../context/AdvancedMD/AdvancedMdContext';

const LabsSummary = () => {
  // 🔹 Id de AdvancedMD desde Redux
  const { advancedMdPatientId } = useSelector(
    state => state.patientMapSelect
  );

  // 🔹 Contexto AdvancedMD (Labs)
  const {
    labs,
    labsLoading,
    labsError,
    loadLabs,
  } = useContext(AdvancedMdContext);

  const [showDetails, setShowDetails] = useState(false);

  // 🔹 Cargar labs cuando tengamos advancedMdPatientId
  useEffect(() => {
    if (!advancedMdPatientId) return;
    loadLabs(advancedMdPatientId);
  }, [advancedMdPatientId, loadLabs]);

  // 🔹 Agrupar resultados por código + descripción
  const groupedLabs = useMemo(() => {
    if (!Array.isArray(labs)) return [];

    const map = new Map();

    labs.forEach(item => {
      const key = `${item.resultCode || ''}|${item.resultDesc || ''}`;
      if (!map.has(key)) {
        map.set(key, {
          resultCode: item.resultCode,
          resultDesc: item.resultDesc,
          values: [],
          units: item.resultUnits || '',
          range: item.resultRange || '',
          status: item.resultStatus || '',
        });
      }
      const group = map.get(key);
      if (item.resultValue && item.resultValue.trim() !== '') {
        group.values.push(item.resultValue);
      }
      if (!group.units && item.resultUnits) group.units = item.resultUnits;
      if (!group.range && item.resultRange) group.range = item.resultRange;
      if (!group.status && item.resultStatus) group.status = item.resultStatus;
    });

    return Array.from(map.values());
  }, [labs]);

  const first = Array.isArray(labs) && labs.length > 0 ? labs[0] : null;

  const totalResults = groupedLabs?.length || 0;

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
      {/* Título */}
      <Typography
        variant="h6"
        sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
      >
        <ScienceIcon color="secondary" />
        <Box ml={1}>Laboratory Results (AdvancedMD)</Box>
      </Typography>

      {/* Meta de la orden de laboratorio */}
      {first && (
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">
              Lab Company
            </Typography>
            <Typography variant="body2">
              {first.companyName || '—'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">
              Observation Date
            </Typography>
            <Typography variant="body2">
              {first.obsDateTime
                ? new Date(first.obsDateTime).toLocaleString()
                : '—'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">
              Specimen Date
            </Typography>
            <Typography variant="body2">
              {first.specDateTime
                ? new Date(first.specDateTime).toLocaleString()
                : '—'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">
              Signed By
            </Typography>
            <Typography variant="body2">
              {first.signBy || '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {first.signAt
                ? new Date(first.signAt).toLocaleString()
                : ''}
            </Typography>
          </Grid>
        </Grid>
      )}

      {labsLoading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Loading laboratory results from AdvancedMD…
        </Typography>
      )}

      {labsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading labs:{' '}
          {labsError.message || 'Unknown error'}
        </Alert>
      )}

      {!labsLoading &&
        !labsError &&
        (!groupedLabs || groupedLabs.length === 0) && (
          <Typography variant="body2" color="text.secondary">
            No laboratory results available for this patient.
          </Typography>
        )}

      {/* 🔹 Línea de resumen + botón de detalles */}
      {!labsLoading &&
        !labsError &&
        groupedLabs &&
        groupedLabs.length > 0 && (
          <Box
            sx={{
              mt: 1,
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {totalResults === 1
                ? '1 lab result'
                : `${totalResults} lab results`}
            </Typography>

            <Button
              size="small"
              variant="outlined"
              onClick={() => setShowDetails(prev => !prev)}
            >
              {showDetails ? 'Hide details' : 'View details'}
            </Button>
          </Box>
        )}

      {/* 🔹 Detalle completo dentro de Collapse */}
      <Collapse in={showDetails}>
        {groupedLabs && groupedLabs.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Result</TableCell>
                <TableCell>Units</TableCell>
                <TableCell>Ref. Range</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedLabs.map(group => (
                <TableRow
                  key={`${group.resultCode}-${group.resultDesc}`}
                >
                  <TableCell>{group.resultCode || '—'}</TableCell>
                  <TableCell>{group.resultDesc || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-line' }}>
                    {group.values.length
                      ? group.values.join('\n')
                      : '—'}
                  </TableCell>
                  <TableCell>{group.units || '—'}</TableCell>
                  <TableCell>{group.range || '—'}</TableCell>
                  <TableCell>
                    {group.status ? (
                      <Chip
                        size="small"
                        label={group.status}
                        color={
                          group.status === 'F'
                            ? 'success'
                            : 'default'
                        }
                        variant={
                          group.status === 'F'
                            ? 'filled'
                            : 'outlined'
                        }
                      />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Collapse>
    </Paper>
  );
};

export default LabsSummary;
